import type { BeforeSend, BeforeSendEvent } from "@vercel/analytics"

/**
 * Web Analytics bills one event per pageview. In September 2026, 1.2M of 1.26M
 * weekly views came from a loop reloading /sounds, which is why the tracker
 * was removed in TES-2207. This filter drops that shape of traffic client-side,
 * before it is sent or billed, so real visitors can be counted again:
 *
 *  - automated browsers (`navigator.webdriver`),
 *  - non-page URLs,
 *  - the same URL again within RELOAD_WINDOW_MS (a reload loop, or a double fire),
 *  - anything past MAX_EVENTS_PER_SESSION in one tab.
 *
 * The counters live in sessionStorage so a full-page reload loop cannot reset
 * them. When storage is unavailable the event is sent: undercounting a real
 * visitor in private mode is worse than the cost it would save.
 */

export const RELOAD_WINDOW_MS = 10_000
export const MAX_EVENTS_PER_SESSION = 50
const STATE_KEY = "tls.va.v1"

interface FilterState {
  count: number
  lastUrl: string
  lastAt: number
}

interface FilterDeps {
  storage: Pick<Storage, "getItem" | "setItem"> | null
  now: () => number
  isAutomated: () => boolean
}

function sessionStore(): FilterDeps["storage"] {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage ?? null
  } catch {
    return null
  }
}

const defaultDeps: Omit<FilterDeps, "storage"> = {
  now: () => Date.now(),
  isAutomated: () => typeof navigator !== "undefined" && navigator.webdriver === true,
}

function readState(storage: NonNullable<FilterDeps["storage"]>): FilterState {
  try {
    const parsed = JSON.parse(storage.getItem(STATE_KEY) ?? "null") as Partial<FilterState> | null
    return { count: parsed?.count ?? 0, lastUrl: parsed?.lastUrl ?? "", lastAt: parsed?.lastAt ?? 0 }
  } catch {
    return { count: 0, lastUrl: "", lastAt: 0 }
  }
}

function isPageUrl(url: string): boolean {
  try {
    const { pathname } = new URL(url, "https://teslalocksound.com")
    return !pathname.startsWith("/api/") && !pathname.startsWith("/_next/")
  } catch {
    return false
  }
}

export function createVercelAnalyticsFilter(deps: Partial<FilterDeps> = {}): BeforeSend {
  const { now, isAutomated } = { ...defaultDeps, ...deps }
  const getStorage = deps.storage !== undefined ? () => deps.storage ?? null : sessionStore
  return (event: BeforeSendEvent) => {
    if (isAutomated() || !isPageUrl(event.url)) return null

    const storage = getStorage()
    if (!storage) return event

    const state = readState(storage)
    const at = now()
    if (state.count >= MAX_EVENTS_PER_SESSION) return null
    if (event.url === state.lastUrl && at - state.lastAt < RELOAD_WINDOW_MS) return null

    try {
      storage.setItem(STATE_KEY, JSON.stringify({ count: state.count + 1, lastUrl: event.url, lastAt: at }))
    } catch {
      // Storage full or blocked mid-session: send the event, as when it was never available.
    }
    return event
  }
}
